import React from 'react';

import {
  GoogleOneTapCredential,
  GoogleOneTapError,
} from '@zapier/auth/src/types';

type GoogleOneTapProps = {
  /**
   * Allows the consumer to inject a custom frame context. This allows you to
   * control the rendering location of One Tap by wrapping it in an `<iframe>`.
   * See `<GoogleOneTapFramed>` if you wish to use this UX.
   */
  frameContext: {
    document: Document | null;
    window: Window | null;
  };

  /**
   * Fired when Google One tap encounters an error.
   */
  onError: (error: GoogleOneTapError) => void;

  /**
   * Fired when Google One tap initiates the auto-login sequence.
   */
  onInitiateAutologin?: () => void;

  /**
   * Fired when the height of the frame containing Google One Tap changes. This
   * is used alongside `frameContext` to contain the presentation of Google One
   * Tap. It is recommended you use `<GoogleOneTapFramed>` if you want this
   * behavior.
   */
  onSetParentHeight?: (height: string | number | null) => void;

  /**
   * Fired when Google One tap successfully logs in.
   */
  onSuccess: (credential: GoogleOneTapCredential) => void;

  /**
   * Enables the auto-login sequence if available. User must have previously
   * logged in with Google One Tap for this to work.
   */
  shouldAutologin: boolean;

  /**
   * Configures the One Tap rendering mode to be either the pop-out style (top
   * right) or bottom sheet style (mobile).
   */
  renderMode: 'bottomSheet' | 'navPopout';

  /** Google Client ID key, typically provided as an environment variable */
  googleClientId?: string;
};

/**
 * Renders the Google One Tap login flow on the current page.
 */
class GoogleOneTap extends React.Component<GoogleOneTapProps> {
  iframeBodyObserver: MutationObserver | null = null;
  iframeChildFrameObserver: MutationObserver | null = null;
  static defaultProps = {
    frameContext: global,
    renderMode: 'navPopout',
  };

  componentDidMount() {
    const { props } = this;

    const { frameContext, renderMode, onSetParentHeight } = this.props;

    frameContext.window!.onGoogleYoloLoad = (googleyolo: GoogleYOLO) => {
      const googleYoloParams = {
        supportedAuthMethods: ['https://accounts.google.com'],
        supportedIdTokenProviders: [
          {
            uri: 'https://accounts.google.com',
            clientId: props.googleClientId,
          },
        ],
      };

      // Unsetting this will result in height being defined as 100%
      googleyolo.setRenderMode(renderMode);
      window.GOOGLE_YOLO = googleyolo;

      if (props.shouldAutologin) {
        if (props.onInitiateAutologin) {
          // Allows us to inform any wrapper components that we are performing
          // an auto-login so that they can hide / disable other login methods.
          props.onInitiateAutologin();
        }

        googleyolo
          .retrieve(googleYoloParams)
          .then(props.onSuccess, props.onError);

        return;
      }

      googleyolo
        .hint({ ...googleYoloParams, context: 'signUp' })
        .then(props.onSuccess, props.onError);
    };

    // Add the Google YOLO script tag to the `document`
    const fileref = frameContext.document!.createElement('script');
    fileref.setAttribute('type', 'text/javascript');
    fileref.setAttribute('src', 'https://smartlock.google.com/client');
    frameContext.document!.head.appendChild(fileref);

    if (onSetParentHeight) {
      const INNER_FRAME_CLASSNAME = 'google-one-tap-sso-frame';

      // This mutation observer is responsible for watching the Google YOLO iframe
      // for height changes. Because this iframe loads async. we will need to
      // calculate at some point in the future what the height of the iframe is.
      // Otherwise we will end up cutting off content (forcing the user to scroll)
      // or displaying too height (bad design).

      this.iframeBodyObserver = new MutationObserver(mutationsList => {
        mutationsList.forEach(mutation => {
          ((mutation.addedNodes as unknown) as Array<
            Node & HTMLIFrameElement
          >).forEach(node => {
            if (
              node &&
              node.nodeName === 'IFRAME' &&
              node.src.includes('smartlock.google.com/iframe/')
            ) {
              node.classList.add(INNER_FRAME_CLASSNAME);

              node.title = 'google one tap frame';

              // We need one more observer to watch the <iframe> that Google Yolo
              // inserted. Its style attribute will be updated with a height that
              // needs to be passed back up to the parent iframe, to avoid any
              // clipping. The height can change if the user is signed in with
              // many Google accounts, and clicks the 'X more accounts' button.

              const attributeObserver = new MutationObserver(
                iframeMutationsList => {
                  const initialHeight =
                    iframeMutationsList[0] &&
                    iframeMutationsList[0].target &&
                    (iframeMutationsList[0].target as HTMLIFrameElement)
                      .style &&
                    parseInt(
                      (iframeMutationsList[0].target as HTMLIFrameElement).style
                        .height!
                    );

                  // User dismissed the SSO frame, close it out
                  if (isNaN(initialHeight)) {
                    attributeObserver.disconnect();
                    return;
                  }

                  // One account is shown
                  if (initialHeight === 271) {
                    onSetParentHeight(208);
                    return;
                  }

                  // Two accounts shown
                  if (initialHeight === 277) {
                    onSetParentHeight(215);
                    return;
                  }

                  // Three or more accounts shown
                  if (initialHeight === 314) {
                    onSetParentHeight(250);
                    return;
                  }

                  // Likely the expanded state, let true height through, minus top padding
                  onSetParentHeight(initialHeight - 18);
                }
              );
              attributeObserver.observe(node, {
                attributes: true,
              });
            }
          });
        });
      });
      this.iframeBodyObserver.observe(frameContext.document!.body, {
        childList: true,
      });

      // Add a <style> for the Google YOLO iframe, once it renders
      const style = frameContext.document!.createElement('style');
      style.type = 'text/css';
      style.appendChild(
        frameContext.document!.createTextNode(
          `.${INNER_FRAME_CLASSNAME} { width: 100% !important; }`
        )
      );
      frameContext.document!.head.appendChild(style);
    }
  }

  componentWillUnmount() {
    // Clean up observers when we leave if they exist
    if (this.iframeChildFrameObserver) {
      this.iframeChildFrameObserver.disconnect();
    }

    if (this.iframeBodyObserver) {
      this.iframeBodyObserver.disconnect();
    }
  }

  render() {
    return null;
  }
}

export default GoogleOneTap;
